import { ReactNode, useCallback, useRef, FC } from 'react';

import { AbortControllerEntry, AbortControllerKey } from '../types/abort-controller.types.ts';

import { AbortControllerContext } from './abort-controller.context.ts';

interface Props {
  children: ReactNode;
}

type AbortControllersRef = Map<AbortControllerKey, AbortControllerEntry>;

export const AbortControllerProvider: FC<Props> = ({ children }) => {
  const abortControllersRef = useRef<AbortControllersRef>(new Map());

  const initializeAbortController = useCallback((key: AbortControllerKey, onAbort?: () => void): AbortController => {
    const entry = abortControllersRef.current.get(key);
    if (entry) {
      entry.controller.abort();
      abortControllersRef.current.delete(key);
    }

    const controller = new AbortController();
    abortControllersRef.current.set(key, { controller, onAbort });
    controller.signal.addEventListener('abort', () => {
      abortControllersRef.current.get(key)?.onAbort?.();
    });
    return controller;
  }, []);

  const abort = useCallback((key: AbortControllerKey) => {
    const entry = abortControllersRef.current.get(key);
    if (entry) {
      entry.controller.abort();
      abortControllersRef.current.delete(key);
    }
  }, []);

  return (
    <AbortControllerContext.Provider value={{ initializeAbortController, abort }}>
      {children}
    </AbortControllerContext.Provider>
  );
};
